/**
 * Saisie / consultation d'une auto-évaluation — look & feel SkillMap V0
 * (accordéons BPML, pills score 0–4, barre de progression, Save Draft).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  ApiRequestError,
  type AssessmentDetailDto,
  type AssessmentLineDto,
  type AssessmentLineFeatureDto,
  type FeatureAssessmentLineDto,
  type TransversalAssessmentLineDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { StatusBadge } from "../components/ui/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { isBpmlHierarchyLeaf } from "../utils/bpml-hierarchy";

type ScoreEntry = { score: number | null; comment: string };
type ScoreMap = Record<string, ScoreEntry>;

type BpGroup = { bp: string; lines: AssessmentLineDto[] };
type MacroGroup = { mp: string; bps: BpGroup[] };

const DEFAULT_SCALE = [
  { level: 0, labelEn: "None", labelFr: "Aucun" },
  { level: 1, labelEn: "Basic", labelFr: "Basique" },
  { level: 2, labelEn: "Intermediate", labelFr: "Intermédiaire" },
  { level: 3, labelEn: "Advanced", labelFr: "Avancé" },
  { level: 4, labelEn: "Expert", labelFr: "Expert" },
];

function scoreClass(score: number | null): string {
  if (score === null || score === undefined) return "";
  return `sm-score-${score}`;
}

// ── Feature badge with hierarchy tooltip ─────────────────────────────────────
function FeatureBadge({ feat, langFr }: { feat: AssessmentLineFeatureDto; langFr: boolean }) {
  const [open, setOpen] = useState(false);
  const label = langFr && feat.labelFr ? feat.labelFr : feat.labelEn;

  const hierarchy = [
    feat.moduleCode && feat.moduleLabel
      ? `${feat.moduleCode} — ${feat.moduleLabel}`
      : feat.moduleCode ?? null,
    feat.subModuleCode && feat.subModuleLabel
      ? `${feat.subModuleCode} — ${feat.subModuleLabel}`
      : feat.subModuleCode ?? null,
  ].filter(Boolean);

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span
        style={{
          fontFamily: "monospace", fontSize: "0.72em", fontWeight: 700,
          background: "#e0f2fe", color: "#0369a1",
          padding: "1px 6px", borderRadius: 4, cursor: "help",
          border: "1px solid #bae6fd", whiteSpace: "nowrap",
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        🔧 {feat.code}
      </span>

      {open && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 6px)", left: 0,
          background: "#1e293b", color: "#f1f5f9",
          borderRadius: 8, padding: "10px 12px",
          minWidth: 220, maxWidth: 320, zIndex: 9999,
          boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
          fontSize: "0.78em", lineHeight: 1.5, pointerEvents: "none",
        }}>
          {/* Hierarchy breadcrumb */}
          {hierarchy.length > 0 && (
            <div style={{ color: "#94a3b8", marginBottom: 4, fontSize: "0.9em" }}>
              {hierarchy.join(" › ")}
            </div>
          )}

          {/* Feature code + label */}
          <div style={{ fontWeight: 700, color: "#7dd3fc", marginBottom: 2 }}>
            {feat.code}
          </div>
          <div style={{ color: "#e2e8f0", marginBottom: feat.description ? 6 : 0 }}>
            {label}
          </div>

          {/* Description */}
          {feat.description && (
            <div style={{
              borderTop: "1px solid #334155", paddingTop: 6,
              color: "#cbd5e1", fontSize: "0.92em",
            }}>
              {feat.description}
            </div>
          )}

          {/* Tooltip arrow */}
          <div style={{
            position: "absolute", top: "100%", left: 12,
            width: 0, height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: "6px solid #1e293b",
          }} />
        </div>
      )}
    </span>
  );
}

function groupBpmlLines(lines: AssessmentLineDto[]): MacroGroup[] {
  const mpMap = new Map<string, Map<string, AssessmentLineDto[]>>();
  for (const line of lines) {
    const mp = line.macroProcess || "—";
    const bp = line.businessProcess || "—";
    if (!mpMap.has(mp)) mpMap.set(mp, new Map());
    const bpMap = mpMap.get(mp)!;
    if (!bpMap.has(bp)) bpMap.set(bp, []);
    bpMap.get(bp)!.push(line);
  }
  return Array.from(mpMap.entries()).map(([mp, bpMap]) => ({
    mp,
    bps: Array.from(bpMap.entries()).map(([bp, groupLines]) => ({ bp, lines: groupLines })),
  }));
}

function significantTv(value: string | null | undefined): string | null {
  if (value == null) return null;
  const t = value.trim();
  if (!t || t === ".") return null;
  return t;
}

/** macro → business → sub (optionnel) → lignes (task au niveau feuille). */
function groupTransversal(lines: TransversalAssessmentLineDto[]): Array<{
  mp: string;
  bps: Array<{
    bp: string;
    subs: Array<{ sub: string | null; lines: TransversalAssessmentLineDto[] }>;
  }>;
}> {
  type SubMap = Map<string, TransversalAssessmentLineDto[]>;
  const mpMap = new Map<string, Map<string, SubMap>>();
  for (const line of lines) {
    const mp = line.macroProcess || "—";
    const bp = line.businessProcess || "—";
    const subKey = significantTv(line.subProcess) ?? "";
    if (!mpMap.has(mp)) mpMap.set(mp, new Map());
    const bpMap = mpMap.get(mp)!;
    if (!bpMap.has(bp)) bpMap.set(bp, new Map());
    const subMap = bpMap.get(bp)!;
    if (!subMap.has(subKey)) subMap.set(subKey, []);
    subMap.get(subKey)!.push(line);
  }
  return Array.from(mpMap.entries()).map(([mp, bpMap]) => ({
    mp,
    bps: Array.from(bpMap.entries()).map(([bp, subMap]) => ({
      bp,
      subs: Array.from(subMap.entries()).map(([sub, groupLines]) => ({
        sub: sub || null,
        lines: groupLines,
      })),
    })),
  }));
}

type FeatureSubGroup = {
  subCode: string | null;
  subLabel: string | null;
  lines: FeatureAssessmentLineDto[];
};
type FeatureModGroup = {
  modCode: string | null;
  modLabel: string | null;
  subs: FeatureSubGroup[];
};

/** N1 (module) → N2 (sous-module) → N3 (fonctionnalité). */
function groupFeatureLines(lines: FeatureAssessmentLineDto[]): FeatureModGroup[] {
  const mods: FeatureModGroup[] = [];
  for (const line of lines) {
    const modKey = line.moduleCode ?? "";
    let mod = mods.find((m) => (m.modCode ?? "") === modKey);
    if (!mod) {
      mod = { modCode: line.moduleCode, modLabel: line.moduleLabel, subs: [] };
      mods.push(mod);
    }
    const subKey = line.subModuleCode ?? "";
    let sub = mod.subs.find((s) => (s.subCode ?? "") === subKey);
    if (!sub) {
      sub = { subCode: line.subModuleCode, subLabel: line.subModuleLabel, lines: [] };
      mod.subs.push(sub);
    }
    sub.lines.push(line);
  }
  return mods;
}

function Chevron({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      className={`sm-assess-chevron${collapsed ? " is-collapsed" : ""}`}
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export function AssessmentReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const [assessment, setAssessment] = useState<AssessmentDetailDto | null>(null);
  const [scores, setScores] = useState<ScoreMap>({});
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirtyEpoch, setDirtyEpoch] = useState(0);
  const [showReturn, setShowReturn] = useState(false);
  const [showForce, setShowForce] = useState(false);
  const [returnComment, setReturnComment] = useState("");
  const [forceReason, setForceReason] = useState("");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scoresRef = useRef(scores);
  const assessmentRef = useRef(assessment);
  const dirtyKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    scoresRef.current = scores;
  }, [scores]);

  useEffect(() => {
    assessmentRef.current = assessment;
  }, [assessment]);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const res = await assessmentApi.get(id);
      let detail = res.assessment;
      if (detail.status === "draft") {
        try {
          const synced = await assessmentApi.syncBpml(id);
          detail = synced.assessment;
        } catch (err) {
          const skipped =
            err instanceof ApiRequestError &&
            (err.status === 403 ||
              err.code === "CAMPAIGN_NOT_OPEN" ||
              err.code === "CAMPAIGN_CLOSED" ||
              err.code === "CAMPAIGN_TOO_EARLY" ||
              err.code === "CAMPAIGN_DEADLINE_PASSED" ||
              err.code === "FORBIDDEN");
          if (!skipped) {
            setError(
              err instanceof ApiRequestError ? err.message : t("sync_bpml_failed")
            );
          }
        }
      }
      setAssessment(detail);
      const map: ScoreMap = {};
      for (const line of detail.lines) {
        // Clé = assessment_line.id (stable même avec plusieurs lignes pour le même bpml)
        map[line.id] = { score: line.score, comment: line.comment ?? "" };
      }
      for (const line of detail.featureLines) {
        map[`f:${line.featureId}`] = {
          score: line.score,
          comment: line.comment ?? "",
        };
      }
      dirtyKeysRef.current = new Set();
      setScores(map);
      setSaved(true);
      setDirtyEpoch(0);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
      setAssessment(null);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const readOnly =
    !assessment ||
    assessment.status !== "draft" ||
    assessment.userId !== user?.id;

  const scale =
    Array.isArray(assessment?.scaleSnapshot) && assessment!.scaleSnapshot!.length
      ? assessment!.scaleSnapshot!
      : DEFAULT_SCALE;

  const flushSave = useCallback(async (): Promise<boolean> => {
    const currentAssessment = assessmentRef.current;
    if (
      !id ||
      !currentAssessment ||
      currentAssessment.status !== "draft" ||
      currentAssessment.userId !== user?.id
    ) {
      return true;
    }
    const dirty = Array.from(dirtyKeysRef.current);
    if (dirty.length === 0) {
      setSaved(true);
      return true;
    }
    const current = scoresRef.current;
    const bpml = dirty
      .filter((k) => !k.startsWith("f:"))
      .map((assessmentLineId) => {
        const v = current[assessmentLineId] ?? { score: null, comment: "" };
        return {
          id: assessmentLineId,   // assessment_line.id (pas bpmlLineId)
          score: v.score,
          comment: v.comment.trim() === "" ? null : v.comment,
        };
      });
    const featureLines = dirty
      .filter((k) => k.startsWith("f:"))
      .map((k) => {
        const v = current[k] ?? { score: null, comment: "" };
        return {
          featureId: k.slice(2),
          score: v.score,
          comment: v.comment.trim() === "" ? null : v.comment,
        };
      });

    setBusy(true);
    setError(null);
    try {
      let latest = currentAssessment;
      if (bpml.length) {
        const res = await assessmentApi.patchLines(id, bpml);
        latest = res.assessment;
      }
      if (featureLines.length) {
        const res = await assessmentApi.patchFeatureLines(id, featureLines);
        latest = res.assessment;
      }
      setAssessment(latest);
      dirtyKeysRef.current = new Set();
      setSaved(true);
      return true;
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("save_failed"));
      return false;
    } finally {
      setBusy(false);
    }
  }, [id, user?.id]);

  useEffect(() => {
    if (readOnly || dirtyEpoch === 0) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void flushSave();
    }, 5000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [dirtyEpoch, readOnly, flushSave]);

  function markDirty(key: string) {
    dirtyKeysRef.current.add(key);
    setSaved(false);
    setDirtyEpoch((n) => n + 1);
  }

  function toggleCollapse(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function setLineScore(key: string, level: number) {
    if (readOnly) return;
    setScores((prev) => ({
      ...prev,
      [key]: { score: level, comment: prev[key]?.comment ?? "" },
    }));
    markDirty(key);
  }

  function setLineComment(key: string, comment: string) {
    if (readOnly) return;
    setScores((prev) => ({
      ...prev,
      [key]: { score: prev[key]?.score ?? null, comment },
    }));
    markDirty(key);
  }

  async function onSubmit() {
    if (!id || !assessment) return;
    const ok = await flushSave();
    if (!ok) return;
    if (!window.confirm(t("confirm_submit"))) return;
    setBusy(true);
    setError(null);
    try {
      const res = await assessmentApi.submit(id);
      setAssessment(res.assessment);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("submit_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onDeleteDraft() {
    if (!id || !assessment) return;
    if (!window.confirm(t("confirm_delete_draft"))) return;
    setBusy(true);
    setError(null);
    try {
      await assessmentApi.deleteDraft(id);
      navigate((user?.accessLevel ?? 0) >= 50 ? "/validation" : "/assessments", { replace: true });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("delete_draft_failed"));
      setBusy(false);
    }
  }

  async function onValidate(reason?: string) {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await assessmentApi.validate(id, reason ? { reason } : undefined);
      setAssessment(res.assessment);
      setShowForce(false);
      setForceReason("");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("validate_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onReturn() {
    if (!id || !returnComment.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await assessmentApi.returnToExpert(id, returnComment.trim());
      setAssessment(res.assessment);
      setShowReturn(false);
      setReturnComment("");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("return_failed"));
    } finally {
      setBusy(false);
    }
  }

  const scoreableBpml = useMemo(
    () => (assessment ? assessment.lines.filter((l) => isBpmlHierarchyLeaf(l, assessment.lines)) : []),
    [assessment]
  );
  const grouped = useMemo(() => groupBpmlLines(scoreableBpml), [scoreableBpml]);
  const featGrouped = useMemo(
    () => (assessment ? groupFeatureLines(assessment.featureLines) : []),
    [assessment]
  );

  if (!assessment && !error) {
    return (
      <div className="sm-page">
        <p className="sm-muted">{t("loading")}</p>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="sm-page">
        <div className="sm-alert sm-alert-error">{error}</div>
        <Link to="/assessments" className="sm-link">
          ← {t("back_to_list")}
        </Link>
      </div>
    );
  }

  const totalLines = scoreableBpml.length + assessment.featureLines.length;
  const scoredLines =
    scoreableBpml.filter((l) => (scores[l.id]?.score ?? null) !== null).length +
    assessment.featureLines.filter(
      (l) => (scores[`f:${l.featureId}`]?.score ?? null) !== null
    ).length;
  const progress = totalLines > 0 ? Math.round((scoredLines / totalLines) * 100) : 0;
  const langFr = i18n.language?.toLowerCase().startsWith("fr");
  const level = user?.accessLevel ?? 0;
  const isManagerOrAdmin = level >= 50;
  const isAdmin = level >= 100;
  const isManager = isManagerOrAdmin && !isAdmin;
  const canValidate =
    isManagerOrAdmin &&
    (assessment.status === "submitted" || (isAdmin && assessment.status === "validated"));
  const showManagerActions = isManagerOrAdmin && assessment.status === "submitted";
  const showAdminReturnValidated = isAdmin && assessment.status === "validated";
  const canDeleteDraft =
    assessment.status === "draft" &&
    !isManager &&
    (isAdmin || assessment.userId === user?.id);

  return (
    <div className="sm-page">
      <PageHeader
        title={`${assessment.domain} — ${assessment.applicationLabel} — ${assessment.year}`}
        subtitle={`${assessment.userName} (${assessment.userInternalId})`}
        actions={
          <div className="sm-form-actions">
            <StatusBadge status={assessment.status} />
            <Link to={isManagerOrAdmin ? "/validation" : "/assessments"} className="sm-btn sm-btn-ghost">
              {isManagerOrAdmin ? t("validation_dashboard") : t("back_to_list")}
            </Link>
            {canDeleteDraft ? (
              <button
                type="button"
                className="sm-btn sm-btn-ghost"
                style={{ color: "#c2410c" }}
                disabled={busy}
                onClick={() => void onDeleteDraft()}
              >
                {t("delete_draft")}
              </button>
            ) : null}
            {!readOnly ? (
              <>
                <button
                  type="button"
                  className="sm-btn sm-btn-secondary"
                  disabled={busy}
                  onClick={() => void flushSave()}
                >
                  {busy ? "…" : t("save_draft")}
                  {saved ? " ✓" : ""}
                </button>
                <button
                  type="button"
                  className="sm-btn sm-btn-primary"
                  disabled={busy}
                  onClick={() => void onSubmit()}
                >
                  {t("submit_assessment")}
                </button>
              </>
            ) : null}
            {showManagerActions ? (
              <>
                <button
                  type="button"
                  className="sm-btn sm-btn-secondary"
                  disabled={busy}
                  onClick={() => setShowReturn(true)}
                >
                  {t("return_to_expert")}
                </button>
                <button
                  type="button"
                  className="sm-btn sm-btn-primary"
                  disabled={busy}
                  onClick={() => void onValidate()}
                >
                  {t("validate")}
                </button>
                {isAdmin ? (
                  <button
                    type="button"
                    className="sm-btn sm-btn-ghost"
                    style={{ color: "#c2410c" }}
                    disabled={busy}
                    onClick={() => setShowForce(true)}
                  >
                    {t("force_validate")}
                  </button>
                ) : null}
              </>
            ) : null}
            {showAdminReturnValidated ? (
              <button
                type="button"
                className="sm-btn sm-btn-ghost"
                style={{ color: "#c2410c" }}
                disabled={busy}
                onClick={() => setShowReturn(true)}
              >
                {t("return_to_expert")}
              </button>
            ) : null}
          </div>
        }
      />

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      {assessment.managerComments?.length ? (
        <div style={{ marginBottom: 16 }}>
          {assessment.managerComments
            .filter((c) => !c.bpmlLineId)
            .map((c) => (
              <div key={c.id} className="sm-alert sm-alert-info" style={{ background: "#fffbeb", borderColor: "#fde68a", color: "#92400e" }}>
                <strong>
                  {c.authorName} — {new Date(c.createdAt).toLocaleDateString()}
                </strong>
                <div style={{ marginTop: 4 }}>{c.comment}</div>
              </div>
            ))}
        </div>
      ) : null}

      {readOnly && assessment.status === "draft" && !canValidate ? (
        <p className="sm-muted">{t("read_only_owner")}</p>
      ) : null}

      {!readOnly ? (
        <div className="sm-assess-progress">
          <div className="sm-progress" aria-hidden>
            <span style={{ width: `${progress}%` }} />
          </div>
          <span className="sm-muted">
            {scoredLines}/{totalLines} {t("lines_scored")} ({progress}%)
          </span>
        </div>
      ) : null}

      <div className="sm-assess-stack">
        {grouped.map(({ mp, bps }) => (
          <div key={mp} className="sm-card sm-assess-block">
            <button
              type="button"
              className="sm-assess-macro"
              onClick={() => toggleCollapse(mp)}
            >
              <span>{mp}</span>
              <Chevron collapsed={collapsed.has(mp)} />
            </button>
            {!collapsed.has(mp)
              ? bps.map(({ bp, lines }) => (
                  <div key={bp}>
                    <div className="sm-assess-bp">{bp}</div>
                    {lines.map((line) => {
                      const key = line.id; // assessment_line.id
                      const v = scores[key] ?? { score: null, comment: "" };
                      return (
                        <div key={line.id} className="sm-assess-line">
                          <div className="sm-assess-line-label">
                            <div>
                              {line.subProcess ? (
                                <>
                                  {line.subProcess}
                                  {line.task && line.task !== "." ? (
                                    <span className="sm-assess-task">{line.task}</span>
                                  ) : null}
                                </>
                              ) : line.task && line.task !== "." ? (
                                <em>{line.task}</em>
                              ) : (
                                <em className="sm-muted">{t("bp_level_placeholder")}</em>
                              )}
                              {line.isCritical ? (
                                <span className="sm-badge-pill sm-badge-red" style={{ marginLeft: 6 }}>
                                  {t("critical")}
                                </span>
                              ) : null}
                              {line.environmentLabel ? (
                                <span style={{
                                  marginLeft: 6, background: "#dbeafe", color: "#1d4ed8",
                                  padding: "1px 8px", borderRadius: 10, fontSize: "0.78em", fontWeight: 600,
                                }}>
                                  {line.environmentLabel}
                                </span>
                              ) : null}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                              <span className="sm-assess-meta">
                                {t("complexity_short", { n: line.complexityIndex })}
                              </span>
                              {line.features && line.features.length > 0 ? (
                                <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                  {line.features.map((feat) => (
                                    <FeatureBadge key={feat.code} feat={feat} langFr={langFr} />
                                  ))}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="sm-assess-scores">
                            {readOnly ? (
                              <span className={`sm-score-pill ${scoreClass(v.score)}`}>
                                {v.score !== null
                                  ? `${v.score} — ${
                                      (langFr
                                        ? scale.find((s) => s.level === v.score)?.labelFr
                                        : scale.find((s) => s.level === v.score)?.labelEn) ??
                                      v.score
                                    }`
                                  : "—"}
                              </span>
                            ) : (
                              scale.map((s) => (
                                <button
                                  key={s.level}
                                  type="button"
                                  title={langFr ? s.labelFr : s.labelEn}
                                  className={`sm-score-btn${
                                    v.score === s.level ? ` ${scoreClass(s.level)}` : ""
                                  }`}
                                  onClick={() => setLineScore(key, s.level)}
                                >
                                  {s.level}
                                </button>
                              ))
                            )}
                          </div>
                          {!readOnly ? (
                            <input
                              className="sm-input sm-assess-comment"
                              placeholder={t("add_comment")}
                              value={v.comment}
                              onChange={(e) => setLineComment(key, e.target.value)}
                            />
                          ) : v.comment ? (
                            <span className="sm-assess-comment-ro">{v.comment}</span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ))
              : null}
          </div>
        ))}
      </div>

      {featGrouped.length > 0 ? (
        <div className="sm-assess-stack" style={{ marginTop: 24 }}>
          <h2 className="sm-assess-tv-title">{t("feature_self_eval")}</h2>
          <p className="sm-alert sm-alert-info">{t("feature_self_eval_sub")}</p>
          {featGrouped.map((mod) => {
            const modKey = `f:${mod.modCode ?? ""}`;
            const modTitle = mod.modCode
              ? mod.modLabel
                ? `${mod.modCode} — ${mod.modLabel}`
                : mod.modCode
              : assessment.applicationLabel ?? "—";
            return (
              <div key={modKey} className="sm-card sm-assess-block">
                <button
                  type="button"
                  className="sm-assess-macro sm-assess-macro-feat"
                  onClick={() => toggleCollapse(modKey)}
                >
                  <span>{modTitle}</span>
                  <Chevron collapsed={collapsed.has(modKey)} />
                </button>
                {!collapsed.has(modKey)
                  ? mod.subs.map((sub) => (
                      <div key={`${modKey}|${sub.subCode ?? ""}`}>
                        {sub.subCode ? (
                          <div className="sm-assess-sub-feat">
                            {sub.subLabel ? `${sub.subCode} — ${sub.subLabel}` : sub.subCode}
                          </div>
                        ) : null}
                        {sub.lines.map((line) => {
                          const fk = `f:${line.featureId}`;
                          const v = scores[fk] ?? { score: null, comment: "" };
                          const label = langFr
                            ? line.featureLabelFr || line.featureLabelEn
                            : line.featureLabelEn;
                          return (
                            <div key={line.id} className="sm-assess-line">
                              <div className="sm-assess-line-label">
                                <div>
                                  <code style={{ fontSize: "0.85em", fontWeight: 600, marginRight: 6 }}>
                                    {line.featureCode}
                                  </code>
                                  {label}
                                </div>
                              </div>
                              <div className="sm-assess-scores">
                                {readOnly ? (
                                  <span className={`sm-score-pill ${scoreClass(v.score)}`}>
                                    {v.score !== null ? String(v.score) : "—"}
                                  </span>
                                ) : (
                                  scale.map((s) => (
                                    <button
                                      key={s.level}
                                      type="button"
                                      title={langFr ? s.labelFr : s.labelEn}
                                      className={`sm-score-btn${
                                        v.score === s.level ? ` ${scoreClass(s.level)}` : ""
                                      }`}
                                      onClick={() => setLineScore(fk, s.level)}
                                    >
                                      {s.level}
                                    </button>
                                  ))
                                )}
                              </div>
                              {!readOnly ? (
                                <input
                                  className="sm-input sm-assess-comment"
                                  placeholder={t("add_comment")}
                                  value={v.comment}
                                  onChange={(e) => setLineComment(fk, e.target.value)}
                                />
                              ) : v.comment ? (
                                <span className="sm-assess-comment-ro">{v.comment}</span>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ))
                  : null}
              </div>
            );
          })}
        </div>
      ) : assessment.applicationId && assessment.status === "draft" ? (
        <div className="sm-assess-stack" style={{ marginTop: 24 }}>
          <h2 className="sm-assess-tv-title">{t("feature_self_eval")}</h2>
          <p className="sm-muted">{t("no_feature_lines")}</p>
        </div>
      ) : null}

      {showReturn ? (
        <div className="sm-modal-backdrop" role="dialog" aria-modal="true">
          <div className="sm-card sm-card-pad" style={{ maxWidth: 480, margin: "10vh auto" }}>
            <h2 style={{ marginTop: 0 }}>{t("return_to_expert")}</h2>
            <p className="sm-muted">{t("mandatory_comment")}</p>
            <textarea
              className="sm-input"
              rows={4}
              value={returnComment}
              onChange={(e) => setReturnComment(e.target.value)}
              placeholder={t("comment")}
            />
            <div className="sm-form-actions" style={{ marginTop: 12 }}>
              <button type="button" className="sm-btn sm-btn-ghost" onClick={() => setShowReturn(false)}>
                {t("cancel")}
              </button>
              <button
                type="button"
                className="sm-btn sm-btn-primary"
                disabled={busy || !returnComment.trim()}
                onClick={() => void onReturn()}
              >
                {t("return_to_expert")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showForce ? (
        <div className="sm-modal-backdrop" role="dialog" aria-modal="true">
          <div className="sm-card sm-card-pad" style={{ maxWidth: 480, margin: "10vh auto" }}>
            <h2 style={{ marginTop: 0 }}>{t("force_validate")}</h2>
            <p className="sm-muted">{t("confirm_force_validate")}</p>
            <textarea
              className="sm-input"
              rows={3}
              value={forceReason}
              onChange={(e) => setForceReason(e.target.value)}
              placeholder={t("reason")}
            />
            <div className="sm-form-actions" style={{ marginTop: 12 }}>
              <button type="button" className="sm-btn sm-btn-ghost" onClick={() => setShowForce(false)}>
                {t("cancel")}
              </button>
              <button
                type="button"
                className="sm-btn sm-btn-primary"
                disabled={busy || !forceReason.trim()}
                onClick={() => void onValidate(forceReason.trim())}
              >
                {t("validate")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
